/**
 * ExpandableText - 可展开/收起的文本组件
 * 用于处理长文本显示，默认显示指定行数，点击可展开查看完整内容
 */

import React, { useState, useRef, useEffect } from 'react'

interface ExpandableTextProps {
  text: string | null | undefined
  maxLines?: number // 默认显示行数，默认 3 行
  className?: string
  expandText?: string // 展开按钮文字
  collapseText?: string // 收起按钮文字
}

const ExpandableText: React.FC<ExpandableTextProps> = ({
  text,
  maxLines = 3,
  className = '',
  expandText = '展开',
  collapseText = '收起',
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [needsExpand, setNeedsExpand] = useState(false)
  const textRef = useRef<HTMLDivElement>(null)

  // 检测文本是否超过指定行数
  useEffect(() => {
    if (!textRef.current || !text) {
      setNeedsExpand(false)
      return
    }

    // 临时取消 line-clamp 限制来获取完整高度
    const el = textRef.current
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20
    const maxHeight = lineHeight * maxLines

    // 先设置为无限制状态获取真实高度
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(el.style as any).lineClamp = 'unset'
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(el.style as any).webkitLineClamp = 'unset'
    const fullHeight = el.scrollHeight

    // 恢复 line-clamp
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(el.style as any).lineClamp = String(maxLines)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(el.style as any).webkitLineClamp = String(maxLines)

    setNeedsExpand(fullHeight > maxHeight + 4) // +4px 容差
  }, [text, maxLines])

  if (!text) {
    return <div className={`${className} min-h-[20px]`}>暂无简介</div>
  }

  return (
    <div className={className}>
      <div
        ref={textRef}
        className={`whitespace-pre-wrap leading-relaxed transition-all ${
          !isExpanded ? `line-clamp-${maxLines}` : ''
        }`}
        style={{
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          WebkitLineClamp: isExpanded ? 'unset' : maxLines,
        }}
      >
        {text}
      </div>
      {needsExpand && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-1 text-sm text-[#FF2442] hover:text-[#FF5B71] transition-colors font-medium"
        >
          {isExpanded ? collapseText : expandText}
        </button>
      )}
    </div>
  )
}

export default ExpandableText